import { API_URL } from '../constants';
import { apiRequest } from '../utils/api';

export interface Post {
  id: string;
  userId: string;
  title: string;
  content?: string;
  mediaUrls: string[];
  mediaTypes: string[];
  isPublic: boolean;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
  };
}

export interface CreatePostData {
  title: string;
  content?: string;
  mediaUrls: string[];
  mediaTypes: string[];
  isPublic: boolean;
}

export interface CreatePostResponse {
  message: string;
  post: Post;
  uploadInfo: {
    walletAddress: string;
    postId: string;
  };
}

export interface GetPostsParams {
  page?: number;
  limit?: number;
  userId?: string;
}

export interface PostsResponse {
  posts: Post[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class PostService {
  private baseUrl = API_URL + '/api/posts';

  async createPost(postData: CreatePostData): Promise<CreatePostResponse> {
    return apiRequest(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postData),
    });
  }

  async getPosts(params: GetPostsParams = {}): Promise<PostsResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.userId) queryParams.append('userId', params.userId);

    const url = `${this.baseUrl}?${queryParams.toString()}`;
    console.log('Fetching posts from:', url);

    try {
      const result = await apiRequest(url);
      console.log('Posts API response:', result);
      return result;
    } catch (error: any) {
      console.error('Posts API error:', error);

      // If authentication fails, try test endpoint to see what URLs look like
      if (error.status === 401 || error.status === 403) {
        console.log('Authentication failed, trying test endpoint...');
        try {
          const testResponse = await fetch(`${this.baseUrl}/test-images`);
          if (testResponse.ok) {
            const testData = await testResponse.json();
            console.log('Test endpoint data:', testData);

            // Return test data in the expected format for debugging
            return {
              posts: testData.posts || [],
              pagination: {
                total: testData.posts?.length || 0,
                page: 1,
                limit: 10,
                totalPages: 1
              }
            };
          }
        } catch (testError) {
          console.error('Test endpoint also failed:', testError);
        }
      }

      throw error;
    }
  }

  async getPost(postId: string): Promise<Post> {
    return apiRequest(`${this.baseUrl}/${postId}`);
  }

  async updatePost(postId: string, postData: Partial<CreatePostData>): Promise<Post> {
    return apiRequest(`${this.baseUrl}/${postId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postData),
    });
  }

  async deletePost(postId: string): Promise<void> {
    await apiRequest(`${this.baseUrl}/${postId}`, {
      method: 'DELETE',
    });
  }
}

const postService = new PostService();
export default postService;